<script>
import axios from 'axios'
import authManager from '@/utils/auth'
import { isWechatBrowser, isMobileDevice } from '@/utils/browser'
import { handleGuestClaimAfterAuth, stashGuestClaimContext } from '@/utils/examClaim'
import { buildWechatQRLoginApiUrl } from '@/utils/loginFlow'
import { isValidUsername, USERNAME_HINT } from '@/utils/username'
import { validatePassword, passwordsMatch, PASSWORD_HINT } from '@/utils/password'
import { resolvePostLoginRoute } from '@/utils/referralAuth'
import { checkReferralCoupon, fetchReferralSettings } from '@/utils/referralCoupon'

const SEND_CODE_COOLDOWN = 60

export default {
  name: 'RegisterView',
  data() {
    return {
      email: '',
      password: '',
      confirmPassword: '',
      username: '',
      emailCode: '',
      couponNo: '',
      couponHint: '',
      referralSettings: null,
      codeCaptcha: '',
      codeCaptchaId: '',
      codeCaptchaImage: '',
      sendingCode: false,
      codeCooldown: 0,
      submitting: false,
      usernameHint: USERNAME_HINT,
      passwordHint: PASSWORD_HINT
    }
  },
  computed: {
    bonusHint() {
      if (!this.referralSettings) return ''
      const ref = (this.referralSettings.refereeBonusCents || 0) / 100
      const reg = (this.referralSettings.referrerBonusCents || 0) / 100
      if (ref <= 0 && reg <= 0) return ''
      return `绑定双享礼券后，您约可得 ¥${ref.toFixed(2)} 礼遇（以券类型为准）`
    },
    hasPendingGuestClaim() {
      return !!(this.$route.query.sid && this.$route.query.examId)
    },
    wechatLoginUrl() {
      const { href, sid, examId, coupon } = this.$route.query
      return buildWechatQRLoginApiUrl({
        origin: window.location.origin,
        href: typeof href === 'string' ? href : '',
        sid: typeof sid === 'string' ? sid : '',
        examId: examId != null ? String(examId) : '',
        coupon: typeof coupon === 'string' ? coupon : ''
      })
    },
    isWechatBrowser2() {
      return isWechatBrowser()
    },
    isMobile() {
      return isMobileDevice()
    },
    wechatOfficialLoginUrl() {
      const { sid, examId } = this.$route.query
      if (sid && examId) {
        const redirect = `${window.location.origin}/h5/exam/result`
        return (
          `/api/v1/wechat/official/base/login?sid=${encodeURIComponent(sid)}` +
          `&examId=${encodeURIComponent(String(examId))}` +
          `&redirect=${encodeURIComponent(redirect)}`
        )
      }
      return '/api/v1/wechat/official/login'
    },
    loginLink() {
      return { path: '/login', query: { ...this.$route.query } }
    },
    postRegisterHref() {
      const href = this.$route.query.href
      if (typeof href === 'string' && href.startsWith('/')) return href
      return '/'
    }
  },
  async created() {
    if (this.hasPendingGuestClaim) {
      stashGuestClaimContext({
        sid: this.$route.query.sid,
        examId: this.$route.query.examId
      })
    }
    await this.refreshCodeCaptcha()
    try {
      this.referralSettings = await fetchReferralSettings()
    } catch {
      this.referralSettings = null
    }
    const couponQuery = this.$route.query.coupon || this.$route.query.couponNo
    if (typeof couponQuery === 'string' && couponQuery.trim()) {
      this.couponNo = couponQuery.trim()
      await this.checkCouponNo()
    }
  },
  beforeUnmount() {
    if (this._codeTimer) clearInterval(this._codeTimer)
  },
  methods: {
    async refreshCodeCaptcha() {
      try {
        const response = await axios.get('/api/v1/user/captcha')
        if (response.data?.data) {
          this.codeCaptchaId = response.data.data.captchaId
          this.codeCaptchaImage = response.data.data.imageBase64
        }
      } catch (err) {
        console.error('Failed to get captcha:', err)
      }
    },
    startCodeCooldown() {
      this.codeCooldown = SEND_CODE_COOLDOWN
      this._codeTimer = setInterval(() => {
        this.codeCooldown -= 1
        if (this.codeCooldown <= 0) {
          clearInterval(this._codeTimer)
          this._codeTimer = null
        }
      }, 1000)
    },
    async sendEmailCode() {
      if (!this.email) {
        this.$message.error('请先填写邮箱')
        return
      }
      if (!this.codeCaptcha) {
        this.$message.error('请填写图形验证码')
        return
      }
      if (this.codeCooldown > 0 || this.sendingCode) return

      this.sendingCode = true
      try {
        const response = await axios.post('/api/v1/user/sendemailcode', {
          email: this.email,
          type: 'register',
          captcha: this.codeCaptcha,
          captchaId: this.codeCaptchaId
        })
        if (response.data.code === 200) {
          this.$message.success('验证码已发送到邮箱')
          this.startCodeCooldown()
          this.codeCaptcha = ''
          await this.refreshCodeCaptcha()
        } else {
          this.$message.error(response.data.message || '发送失败')
          await this.refreshCodeCaptcha()
        }
      } catch (err) {
        this.$message.error(err.response?.data?.message || '发送失败')
        await this.refreshCodeCaptcha()
      } finally {
        this.sendingCode = false
      }
    },
    async handleRegister() {
      if (!this.email || !this.password || !this.confirmPassword || !this.username || !this.emailCode) {
        this.$message.error('请填写完整信息')
        return
      }
      if (!isValidUsername(this.username)) {
        this.$message.error(USERNAME_HINT)
        return
      }
      if (!passwordsMatch(this.password, this.confirmPassword)) {
        this.$message.error('两次输入的密码不一致')
        return
      }
      const pwdCheck = validatePassword(this.password)
      if (!pwdCheck.ok) {
        this.$message.error(pwdCheck.message)
        return
      }

      this.submitting = true
      try {
        const payload = {
          username: this.username,
          email: this.email,
          password: this.password,
          confirmPassword: this.confirmPassword,
          emailCode: this.emailCode
        }
        const no = (this.couponNo || '').trim()
        if (no) payload.couponNo = no

        const regResp = await axios.post('/api/v1/user/register', payload)
        if (regResp.data.code !== 200 || !regResp.data.data?.accessToken) {
          this.$message.error(regResp.data.message || '注册失败')
          return
        }

        const loginData = regResp.data.data
        await authManager.handleLoginSuccess(loginData)
        this.$root.login(loginData)

        try {
          const claimStatus = await handleGuestClaimAfterAuth(this.$router, this.$route)
          if (claimStatus === 'claimed') return
          if (claimStatus === 'recharge') {
            this.$message.success(loginData.referralMessage || '注册成功')
            return
          }
        } catch (claimErr) {
          this.$message.error(claimErr.message || '生成报告失败')
          return
        }

        const msg = loginData.referralMessage || (loginData.referralBound ? '注册并绑定礼券成功' : '注册成功')
        this.$message.success(msg)

        if (loginData.referralBound) {
          this.$router.push(this.postRegisterHref)
          return
        }
        const target = await resolvePostLoginRoute(this.postRegisterHref)
        this.$router.push(target)
      } catch (err) {
        this.$message.error(err.response?.data?.message || '注册失败')
      } finally {
        this.submitting = false
      }
    },
    async checkCouponNo() {
      const no = (this.couponNo || '').trim()
      if (!no) {
        this.couponHint = ''
        return
      }
      try {
        const data = await checkReferralCoupon(no)
        if (data.valid) {
          this.couponHint = data.ownerNickname
            ? `推荐人：${data.ownerNickname}，礼券可用`
            : '礼券可用'
        } else {
          this.couponHint = data.message || '礼券无效'
        }
      } catch {
        this.couponHint = ''
      }
    },
    onWechatClick() {
      if (this.hasPendingGuestClaim) {
        stashGuestClaimContext({
          sid: this.$route.query.sid,
          examId: this.$route.query.examId
        })
      }
    }
  }
}
</script>

<template>
  <div class="register" :class="{ 'register--mobile': isMobile }">
    <h2 v-if="!isMobile">注册账号</h2>

    <el-alert
      v-if="hasPendingGuestClaim"
      type="info"
      :closable="false"
      show-icon
      class="guest-alert"
      title="您已完成测评"
      description="注册并登录后，将自动为您生成测评报告。"
    />

    <form @submit.prevent="handleRegister">
      <div>
        <label for="email">邮箱</label>
        <input id="email" v-model="email" type="email" required autocomplete="email">
      </div>

      <div class="captcha-container">
        <label for="codeCaptcha">图形验证码（发送邮箱验证码前填写）</label>
        <div class="captcha-input-group">
          <input id="codeCaptcha" v-model="codeCaptcha" type="text" autocomplete="off">
          <img
            :src="codeCaptchaImage"
            alt="Captcha"
            class="captcha-image"
            @click="refreshCodeCaptcha"
          >
        </div>
      </div>

      <div class="email-code-row">
        <div class="email-code-field">
          <label for="emailCode">邮箱验证码</label>
          <input id="emailCode" v-model="emailCode" type="text" required maxlength="6" autocomplete="one-time-code">
        </div>
        <button
          type="button"
          class="send-code-btn"
          :disabled="codeCooldown > 0 || sendingCode"
          @click="sendEmailCode"
        >
          {{ codeCooldown > 0 ? `${codeCooldown}s` : '发送验证码' }}
        </button>
      </div>

      <div>
        <label for="username">用户名</label>
        <input id="username" v-model="username" type="text" required autocomplete="username">
        <p class="field-hint">{{ usernameHint }}</p>
      </div>

      <div>
        <label for="password">密码</label>
        <input id="password" v-model="password" type="password" required minlength="8" autocomplete="new-password">
        <p class="field-hint">{{ passwordHint }}</p>
      </div>

      <div>
        <label for="confirmPassword">确认密码</label>
        <input
          id="confirmPassword"
          v-model="confirmPassword"
          type="password"
          required
          minlength="8"
          autocomplete="new-password"
        >
      </div>

      <div class="coupon-field">
        <label for="couponNo">礼券号（选填）</label>
        <input
          id="couponNo"
          v-model="couponNo"
          type="text"
          autocomplete="off"
          placeholder="有分享链接可自动填入；注册成功即绑定"
          @blur="checkCouponNo"
        >
        <p v-if="couponHint" class="field-hint">{{ couponHint }}</p>
        <p v-else-if="bonusHint" class="field-hint">{{ bonusHint }}</p>
        <p class="field-hint">也可注册后在「绑定礼券」页补填（限注册后 {{ referralSettings?.bindWithinDaysAfterReg || 7 }} 天内）</p>
      </div>

      <button type="submit" class="submit-btn" :disabled="submitting">
        {{ submitting ? '注册中…' : '注册' }}
      </button>
    </form>

    <div class="alt-auth" :class="{ 'alt-auth--mobile': isMobile }">
      <p>已有账号？<router-link :to="loginLink">去登录</router-link></p>
      <template v-if="!isMobile || isWechatBrowser2">
        <h3 v-if="!isMobile">或使用微信</h3>
        <a
          v-if="!isWechatBrowser2 && !isMobile"
          :href="wechatLoginUrl"
          class="wechat-btn"
          @click="onWechatClick"
        >
          <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="WeChat" class="wechat-icon">
          微信扫码登录
        </a>
        <a
          v-if="isWechatBrowser2"
          :href="wechatOfficialLoginUrl"
          class="wechat-btn wechat-btn--block"
          @click="onWechatClick"
        >
          <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="WeChat" class="wechat-icon">
          微信一键登录
        </a>
      </template>
      <p v-else class="mobile-wechat-hint">
        <a :href="wechatLoginUrl" class="link-wechat" @click="onWechatClick">微信登录</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
.register {
  max-width: 440px;
  margin: 0 auto;
  padding: 20px;
}

.guest-alert {
  margin-bottom: 20px;
}

form > div {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.coupon-field {
  padding: 12px;
  background: #f8faf9;
  border-radius: 8px;
  border: 1px solid #e8eee8;
}

.email-code-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.email-code-field {
  flex: 1;
}

.send-code-btn {
  flex-shrink: 0;
  padding: 8px 12px;
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: 0;
}

.send-code-btn:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
}

.captcha-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-image {
  height: 40px;
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.submit-btn {
  width: 100%;
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;
}

.submit-btn:hover:not(:disabled) {
  background-color: #3aa876;
}

.submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.alt-auth {
  margin-top: 28px;
  text-align: center;
}

.alt-auth h3 {
  color: #666;
  margin: 20px 0 12px;
}

.wechat-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #07c160;
  color: white;
  text-decoration: none;
  border-radius: 4px;
}

.wechat-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
}

.register--mobile {
  max-width: none;
  margin: 0;
  padding: 16px;
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px 12px 0 0;
}

.alt-auth--mobile {
  margin-top: 16px;
}

.wechat-btn--block {
  display: flex;
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
}

.mobile-wechat-hint {
  margin-top: 12px;
  text-align: center;
  font-size: 14px;
}

.link-wechat {
  color: #07c160;
  text-decoration: none;
}
</style>
